// autopilot-abort-challenge-command.ts
//
// Phase 04 [B2/B8/N4/N16]: `avatar autopilot abort` two-step challenge,
// split out of autopilot-control-directive-commands.ts's old single-shot
// `abort` block. Step 1 (no --confirm) issues the challenge AND writes a
// directive carrying `challenge_id` into the inbox — even when the
// supervisor is not running — so the event lands in the durable journal
// once a `run` picks it up (B2, journaled by supervisor-halt-directive-
// step.ts's applyCliIssuedAbortDirective). Step 2 (--confirm <id>)
// pre-validates before writing `abort_confirm`; format validation itself
// lives in abort-challenge.ts's challengePath (N2), shared with every
// other reader/deleter of a challenge file.

import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { Command } from "commander";
import { writeMachineReadableJson } from "../machine-readable-json-output-contract.js";
import { log } from "../terminal-logger.js";
import {
  AbortConfirmRejectedError,
  confirmAbortChallenge,
  issueAbortChallenge,
} from "./abort-challenge.js";
import {
  type ControlDirectiveDeps,
  resolveCliActorId,
  writeControlDirective,
} from "./autopilot-control-directive-shared.js";
import { writeDirective } from "./directive-inbox.js";
import { resolveMission } from "./mission-resolver.js";
import { ABORT_CHALLENGE_MISSION_REVISION } from "./supervisor-halt-directive-step.js";

const DEFAULT_ABORT_CHALLENGE_TTL_SEC = 300;

/** Best-effort read of `abort_challenge_ttl_sec` from the control root's snapshot — same "never fatal for a CLI read" posture as status-effective-config-readers.ts. */
async function readAbortChallengeTtlSec(controlRoot: string): Promise<number> {
  try {
    const raw = await fs.readFile(join(controlRoot, "effective-config.json"), "utf8");
    const parsed = JSON.parse(raw) as { abort_challenge_ttl_sec?: unknown };
    return typeof parsed.abort_challenge_ttl_sec === "number" && parsed.abort_challenge_ttl_sec > 0
      ? parsed.abort_challenge_ttl_sec
      : DEFAULT_ABORT_CHALLENGE_TTL_SEC;
  } catch {
    return DEFAULT_ABORT_CHALLENGE_TTL_SEC;
  }
}

async function runAbortStep1(
  deps: ControlDirectiveDeps,
  workspaceRoot: string,
  missionFlag: string | undefined,
  jsonOut: boolean,
): Promise<void> {
  const resolved = await resolveMission({ workspaceRoot, explicitMissionId: missionFlag });
  const actor = resolveCliActorId();
  const ttlSec = await readAbortChallengeTtlSec(resolved.controlRoot);
  const challenge = await issueAbortChallenge({
    controlRoot: resolved.controlRoot,
    missionId: resolved.missionId,
    actorId: actor,
    missionRevision: ABORT_CHALLENGE_MISSION_REVISION,
    ttlSec,
  });
  const confirmCommand = `avatar autopilot abort --mission ${resolved.missionId} --confirm ${challenge.challenge_id}`;

  let receipt: { directive_id: string } | undefined;
  try {
    receipt = await writeDirective({
      controlRoot: resolved.controlRoot,
      machineLockPath: deps.machineLockPath,
      directive: {
        mission_id: resolved.missionId,
        kind: "abort",
        source: "cli_local",
        actor_id: actor,
        payload: { challenge_id: challenge.challenge_id, requested_at: new Date().toISOString() },
      },
    });
  } catch (err) {
    // [B2] challenge is still valid and its id already about to be printed —
    // only the durable journal event is at risk, so warn loudly (exit != 0)
    // instead of losing the id the operator needs for step 2.
    log.error(
      `CẢNH BÁO: challenge vẫn hợp lệ nhưng ghi directive thất bại — thiếu event journal (${err instanceof Error ? err.message : String(err)}).`,
    );
    process.exitCode = 1;
  }

  if (jsonOut) {
    writeMachineReadableJson({
      command: "autopilot abort",
      challenge_id: challenge.challenge_id,
      expires_at: challenge.expires_at,
      confirm_command: confirmCommand,
      receipt,
    });
  } else {
    log.info(
      `Abort challenge ${challenge.challenge_id} — hạn ${challenge.expires_at}. Xác nhận: ${confirmCommand}`,
    );
  }
}

async function runAbortStep2(
  deps: ControlDirectiveDeps,
  workspaceRoot: string,
  missionFlag: string | undefined,
  challengeId: string,
  jsonOut: boolean,
): Promise<void> {
  try {
    await confirmAbortChallenge({
      controlRoot: (await resolveMission({ workspaceRoot, explicitMissionId: missionFlag }))
        .controlRoot,
      challengeId,
      actorId: resolveCliActorId(),
      missionRevision: ABORT_CHALLENGE_MISSION_REVISION,
    });
  } catch (err) {
    if (!(err instanceof AbortConfirmRejectedError)) throw err;
    log.error(`Abort confirm bị từ chối: ${err.message}`);
    process.exitCode = 1;
    return;
  }
  await writeControlDirective(
    deps,
    workspaceRoot,
    missionFlag,
    "abort_confirm",
    { challenge_id: challengeId, requested_at: new Date().toISOString() },
    jsonOut,
  );
}

export function registerAutopilotAbortChallengeCommand(
  autopilot: Command,
  deps: ControlDirectiveDeps,
): void {
  autopilot
    .command("abort")
    .description("Hai bước huỷ mission: bước 1 trả challenge, --confirm xác nhận")
    .option("--mission <id>", "Mission ID")
    .option("--confirm <id>", "Challenge ID để xác nhận bước 2")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(async (opts: { mission?: string; confirm?: string; repo?: string; json?: boolean }) => {
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      if (opts.confirm) {
        await runAbortStep2(deps, workspaceRoot, opts.mission, opts.confirm, !!opts.json);
      } else {
        await runAbortStep1(deps, workspaceRoot, opts.mission, !!opts.json);
      }
    });
}
